// ----------------------------------------------------------------------------
// SavedContractsContext
// ----------------------------------------------------------------------------
// Shared client-side state for "Saved Contracts". Lives at the app root so the
// drawer's Save button and the Saved Contracts view stay in sync.
//
// Replace the in-memory state with a useEffect-driven fetch + POST/DELETE to a
// real `/api/contracts/saved` endpoint when wiring a live backend.
// ----------------------------------------------------------------------------

import React, { createContext, useCallback, useContext, useMemo, useState } from "react";
import { SAVED_CONTRACTS } from "../data/mockData";

const SavedContractsContext = createContext(null);

// Build a saved-contract record from a bid row. Keeps the saved view's data
// shape stable regardless of where the save action originated.
const toSavedRecord = (bid) => ({
  id: bid.id,
  title: bid.title,
  agency: bid.agency,
  savedOn: new Date().toISOString().slice(0, 10),
  note: "Saved from Active Bids ledger.",
});

export const SavedContractsProvider = ({ children }) => {
  const [items, setItems] = useState(SAVED_CONTRACTS);

  const isSaved = useCallback(
    (id) => items.some((c) => c.id === id),
    [items],
  );

  const addBid = useCallback((bid) => {
    setItems((prev) =>
      prev.some((c) => c.id === bid.id) ? prev : [toSavedRecord(bid), ...prev],
    );
  }, []);

  const removeById = useCallback((id) => {
    setItems((prev) => prev.filter((c) => c.id !== id));
  }, []);

  const toggleBid = useCallback(
    (bid) => {
      let next;
      setItems((prev) => {
        if (prev.some((c) => c.id === bid.id)) {
          next = "removed";
          return prev.filter((c) => c.id !== bid.id);
        }
        next = "added";
        return [toSavedRecord(bid), ...prev];
      });
      return next;
    },
    [],
  );

  const value = useMemo(
    () => ({ items, isSaved, addBid, removeById, toggleBid }),
    [items, isSaved, addBid, removeById, toggleBid],
  );

  return (
    <SavedContractsContext.Provider value={value}>
      {children}
    </SavedContractsContext.Provider>
  );
};

export const useSavedContracts = () => {
  const ctx = useContext(SavedContractsContext);
  if (!ctx)
    throw new Error("useSavedContracts must be used within SavedContractsProvider");
  return ctx;
};
